import { useContext, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import AuthContext from 'contexts/AuthContext';
import UnidadeOrganizacional from 'model/indicadores/UnidadeOrganizacional';
import { DiagnosticoEmissaoDireta, UnidadeOrganizacionalCertificado } from 'model/fiscal/UnidadeOrganizacionalCertificado';
import UnidadeOrganizacionalService from 'service/indicadores/UnidadeOrganizacionalService';
import UnidadeOrganizacionalCertificadoService from 'service/fiscal/UnidadeOrganizacionalCertificadoService';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';

const service = new UnidadeOrganizacionalCertificadoService();
const unidadesService = new UnidadeOrganizacionalService();

function mensagemErro(error: unknown): string {
    if (isAxiosError<{message?: string; detail?: string; mensagem?: string}>(error)) {
        return error.response?.data?.detail || error.response?.data?.message || error.response?.data?.mensagem
            || 'Não foi possível concluir a operação. Confira o certificado e a configuração do servidor.';
    }
    return 'Não foi possível concluir a operação.';
}

export default function UnidadeOrganizacionalCertificadoCadastro() {
    const { acoes } = useContext(AuthContext);
    const permitido = acoes?.some(a => a.sigla === 'UNIDADE_ORGANIZACIONAL_SISTEMA_EMISSAO_CONFIGURAR');
    const [unidades, setUnidades] = useState<UnidadeOrganizacional[]>([]);
    const [unidade, setUnidade] = useState<number | null>(null);
    const [certificados, setCertificados] = useState<UnidadeOrganizacionalCertificado[]>([]);
    const [diagnostico, setDiagnostico] = useState<DiagnosticoEmissaoDireta | null>(null);
    const [arquivo, setArquivo] = useState<File | null>(null);
    const [senha, setSenha] = useState('');
    const [ocupado, setOcupado] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [bloquear, setBloquear] = useState<UnidadeOrganizacionalCertificado | null>(null);
    const upload = useRef<FileUpload>(null);

    useEffect(() => {
        if (!permitido) return;
        let ativo = true;
        unidadesService.getAll().then(u => { if (ativo) setUnidades(u); })
            .catch(() => { if (ativo) setErro('Não foi possível carregar as unidades.'); });
        return () => { ativo = false; };
    }, [permitido]);

    useEffect(() => {
        setCertificados([]); setDiagnostico(null); setSenha(''); setArquivo(null);
        setErro(''); setSucesso(''); upload.current?.clear();
        if (!unidade || !permitido) return;
        let ativo = true;
        setCarregando(true);
        Promise.all([service.listar(unidade), service.diagnosticar(unidade)])
            .then(([lista, dados]) => { if (ativo) { setCertificados(lista); setDiagnostico(dados); } })
            .catch(e => { if (ativo) setErro(mensagemErro(e)); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [unidade, permitido]);

    async function salvar() {
        if (!unidade || !arquivo || !senha || ocupado) return;
        setOcupado(true); setErro(''); setSucesso('');
        try {
            await service.cadastrar(unidade, arquivo, senha);
            setSenha(''); setArquivo(null); upload.current?.clear();
            setSucesso('Certificado cadastrado. O mesmo certificado será utilizado em homologação e produção.');
            setCertificados(await service.listar(unidade));
        } catch (e) { setErro(mensagemErro(e)); }
        finally { setSenha(''); setOcupado(false); }
    }

    async function confirmarBloqueio() {
        if (!unidade || !bloquear || ocupado) return;
        setOcupado(true); setErro(''); setSucesso('');
        try {
            await service.bloquear(unidade, bloquear.id);
            setBloquear(null); setSucesso('Certificado bloqueado para novos usos.');
            setCertificados(await service.listar(unidade));
        } catch (e) { setErro(mensagemErro(e)); }
        finally { setOcupado(false); }
    }

    if (!permitido) return <Message severity="warn" text="Você não tem permissão para administrar certificados." />;
    return <div className="p-3">
        <h2>Certificados da unidade organizacional</h2>
        <p>Cadastre um certificado A1 (.pfx ou .p12). Uma substituição preserva o histórico e desativa a versão anterior.</p>
        <label htmlFor="certificado-unidade">Unidade organizacional</label>
        <Dropdown inputId="certificado-unidade" value={unidade} options={unidades} optionLabel="nome" optionValue="id"
            filter placeholder="Selecione a unidade" disabled={ocupado} onChange={e => setUnidade(e.value)} className="w-100 mb-3" />
        {erro && <Message severity="error" text={erro} className="w-100 mb-3" />}
        {sucesso && <Message severity="success" text={sucesso} className="w-100 mb-3" />}
        {unidade && <>
            <div className="mb-3">
                <FileUpload ref={upload} name="certificado" accept=".pfx,.p12" maxFileSize={1048576} multiple={false}
                    disabled={ocupado || carregando} customUpload chooseLabel="Selecionar certificado" cancelLabel="Limpar"
                    uploadOptions={{style: {display: 'none'}}} onSelect={e => setArquivo(e.files[0] || null)}
                    onClear={() => setArquivo(null)} onRemove={() => setArquivo(null)}
                    emptyTemplate={<p>Arquivo PFX/P12 de até 1 MB.</p>} />
            </div>
            <label htmlFor="certificado-senha">Senha do certificado</label>
            <div className="mb-3"><Password inputId="certificado-senha" value={senha} feedback={false} toggleMask
                autoComplete="new-password" maxLength={1024} disabled={ocupado || carregando} onChange={e => setSenha(e.target.value)} /></div>
            <Button label={certificados.some(c => c.ativo) ? 'Substituir certificado ativo' : 'Cadastrar certificado'}
                type="button" loading={ocupado} disabled={!arquivo || !senha || carregando} onClick={salvar} />
            <DataTable value={certificados} loading={carregando} className="mt-3" emptyMessage="Nenhum certificado cadastrado.">
                <Column field="cnpjTitular" header="CNPJ do titular" />
                <Column field="titular" header="Titular" />
                <Column header="Válido até" body={(c: UnidadeOrganizacionalCertificado) => new Date(c.fimValidade).toLocaleString('pt-BR')} />
                <Column header="Situação" body={(c: UnidadeOrganizacionalCertificado) => !c.ativo ? 'Inativo' : c.vencido ? 'Vencido' : 'Ativo'} />
                <Column header="Ações" body={(c: UnidadeOrganizacionalCertificado) => c.ativo && <Button type="button"
                    label="Bloquear" severity="danger" outlined disabled={ocupado} onClick={() => setBloquear(c)} />} />
            </DataTable>
            {diagnostico && <div className="mt-3">
                <Message severity="info" text="A emissão direta está em preparação: NF-e modelo 55, Regime Normal e origem em SC. O cadastro do certificado ainda não ativa a transmissão." />
                {diagnostico.pendenciasCadastrais.length > 0 ? <><p>Pendências no cadastro da unidade:</p>
                    <ul>{diagnostico.pendenciasCadastrais.map(p => <li key={p}>{p}</li>)}</ul></>
                    : <p>Dados cadastrais necessários preenchidos. A habilitação fiscal será verificada nas próximas etapas.</p>}
            </div>}
        </>}
        <Dialog header="Bloquear certificado" visible={!!bloquear} onHide={() => !ocupado && setBloquear(null)}
            footer={<Button type="button" label="Confirmar bloqueio" severity="danger" loading={ocupado} onClick={confirmarBloqueio} />}>
            O certificado deixará de ser utilizado em novas operações da unidade, tanto em homologação quanto em produção.
        </Dialog>
    </div>;
}
